import React, { useState, useEffect } from "react";
import { Upload } from 'tus-js-client';
import axiosInstance from "../api/axiosInterceptor.js"; // adjust path as needed
import axios from 'axios';
import SyncLoader from "../components/Loader";
import "../styles/Chapters.css";

const ONBOARDING_CHAPTER_TITLE = "Onboarding Video";
const ONBOARDING_CHAPTER_DESCRIPTION = "Onboarding Video";
const ONBOARDING_VIDEO_META = {
  title: "onboarding",
  description: "onboarding",
  language: "en",
  is_free: true,
};
const INTRODUCTION_CHAPTER_TITLE = "Introduction(Common section)";
const INTRODUCTION_CHAPTER_DESCRIPTION = "Introduction(Common section)";
const normalizeTitle = (title = "") => title.trim().toLowerCase();
const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "de", label: "German" },
  { value: "ur", label: "Urdu" },
  { value: "ur_roman", label: "Roman Urdu" },
  { value: "pa", label: "Punjabi" },
  { value: "hi", label: "Hindi" },
  { value: "it", label: "Italian" },
  { value: "ps", label: "Pashtu" },
];

const ChapterTable = () => {
  const [chapters, setChapters] = useState([]);
  const [videoData, setVideoData] = useState([]);
  const [activeChapter, setActiveChapter] = useState(null);
  const [showVideos, setShowVideos] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editVideo, setEditVideo] = useState(null);
  const [isLoading, setIsLoading] = useState(false); 
  const [uploadProgress, setUploadProgress] = useState(null);
  const [transstatus,setTransStatus]=useState(null)
  const [videoToPlay, setVideoToPlay] = useState(null); // for playing video
  const [isChapterModalOpen, setIsChapterModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadByLink, setUploadByLink] = useState(false);
  const [togglingVideoId, setTogglingVideoId] = useState(null);
  // const [newChapterTitle, setNewChapterTitle] = useState("");
  // const [newChapterDescription, setNewChapterDescription] = useState("");
  const [editChapter, setEditChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [chapterType, setChapterType] = useState("normal");
  const itemsPerPage = 5;
  // Fetch chapters on mount
  useEffect(() => {
    const fetchChapters = async () => {
      setIsLoading(true);
      try {
        const res = await axiosInstance.get("/admin/chapters/");
        setChapters(res.data);
      } catch (err) {
        console.error("Failed to fetch chapters", err);
      }
      finally {
        setIsLoading(false); // Stop the loader
      }
    };
    fetchChapters();
  }, []);
  const filteredChapters = chapters.filter((chapter) =>
    (chapter.title || "").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const hasOnboardingChapter = chapters.some(
    (chapter) => normalizeTitle(chapter.title) === normalizeTitle(ONBOARDING_CHAPTER_TITLE)
  );
  const hasIntroductionChapter = chapters.some(
    (chapter) => normalizeTitle(chapter.title) === normalizeTitle(INTRODUCTION_CHAPTER_TITLE)
  );
  const getChapterPriority = (title) => {
    const normalizedTitle = normalizeTitle(title);
    if (normalizedTitle === normalizeTitle(ONBOARDING_CHAPTER_TITLE)) return 0;
    if (normalizedTitle === normalizeTitle(INTRODUCTION_CHAPTER_TITLE)) return 1;
    return 2;
  };
  const getChapterKind = (title) => {
    const priority = getChapterPriority(title);
    if (priority === 0) return "onboarding";
    if (priority === 1) return "introduction";
    return "normal";
  };
  const sortedFilteredChapters = React.useMemo(() => {
    return filteredChapters
      .map((chapter, index) => ({ chapter, index }))
      .sort((a, b) => {
        const priorityDiff = getChapterPriority(a.chapter.title) - getChapterPriority(b.chapter.title);
        if (priorityDiff !== 0) return priorityDiff;
        return a.index - b.index;
      })
      .map((item) => item.chapter);
  }, [filteredChapters]);
  const chapterTitleExists = (title, excludeId = null) =>
    chapters.some((chapter) => {
      if (excludeId !== null && chapter.id === excludeId) return false;
      return normalizeTitle(chapter.title) === normalizeTitle(title);
    });
  const specialChapterConfig =
    chapterType === "onboarding"
      ? {
          label: "Onboarding chapter",
          chapterTitle: ONBOARDING_CHAPTER_TITLE,
          chapterDescription: ONBOARDING_CHAPTER_DESCRIPTION,
          requiresVideoLink: true,
          videoMeta: ONBOARDING_VIDEO_META,
          videoLinkPlaceholder: "Onboarding Vimeo Link",
        }
      : chapterType === "introduction"
      ? {
          label: "Introduction(Common section)",
          chapterTitle: INTRODUCTION_CHAPTER_TITLE,
          chapterDescription: INTRODUCTION_CHAPTER_DESCRIPTION,
          requiresVideoLink: false,
        }
      : null;

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = sortedFilteredChapters.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedFilteredChapters.length / itemsPerPage);
  const getVimeoVideoId = (uri) => {
    const parts = uri.split("/");
    return parts[parts.length - 1]; // "123456789"
  };

  const getVimeoThumbnailUrl = async (vimeoId) => {
    setIsLoading(true);
    try {
      const res = await fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${vimeoId}`);
      const data = await res.json();
      return data.thumbnail_url;
    } catch (error) {
      console.error("Failed to fetch Vimeo thumbnail", error);
      return null;
    }
    finally{
      setIsLoading(false);
    }
  };
  
const handleLinkUpload = async (e) => {
  setIsLoading(true);
  e.preventDefault();
  const form = e.target;
  const title = form.title.value;
  const description = form.description.value;
  const language = form.language.value;
  const isFree = form.is_free.checked;
  const videoLink = form.video_link.value;
  const chapterId = activeChapter.id;

  if (!videoLink) {
    alert("Video link is required.");
    return;
  }

  try {
    const metaForm = new FormData();
    metaForm.append("title", title);
    metaForm.append("description", description);
    metaForm.append("language", language);
    metaForm.append("is_free", isFree ? "True" : "False");
    metaForm.append("chapter", chapterId);
    metaForm.append("video_url", videoLink);

    const payload = {
      "vimeo_url": videoLink,
      "title":title,
      "description":description,
      "language":language,
      "is_free": isFree
  };

    const endpoint = `/admin/chapters/${chapterId}/add-existing-video/`;

    await axiosInstance.post(endpoint, payload);
    const endpoint2= `/admin/chapters/${chapterId}/videos/`

    const res = await axiosInstance.get(endpoint2);

    setVideoData(res.data);
    setIsModalOpen(false);
    setUploadByLink(false);
  } catch (err) {
    console.error("Failed to save video by link:", err);
    alert("Saving video by link failed.");
  }
  finally{
    setIsLoading(false);
  }
};
  const handleOpenChapter = async (chapter) => {
    setIsLoading(true);
    setActiveChapter(chapter);
    try {
      const res = await axiosInstance.get(`/admin/chapters/${chapter.id}/videos/`);
      // const videos = res.data;
      // const updatedVideos = await Promise.all(
      //   videos.map(async (video) => {
      //     const vimeoId = getVimeoVideoId(video.vimeo_video_url);
      //     const thumb = await getVimeoThumbnailUrl(vimeoId);
      //     return { ...video, thumbnail: thumb }; // overwrite or set fallback
      //   })
      // );
      setVideoData(res.data);
      // setVideoData(updatedVideos);
      setShowVideos(true);
    } catch (err) {
      console.error("Failed to fetch videos", err);
    }
    finally{
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setShowVideos(false);
    setActiveChapter(null);
    setVideoData([]);
  };


  // const handleEditVideo = (video) => {
  //   setEditVideo(video);
  //   setIsModalOpen(true);
  // };

  const handleAddVideo = () => {
    setEditVideo(null);
    setIsModalOpen(true);
  };
  const handleAddChapter = async (e) => {
    setEditChapter(null); // New chapter
    setChapterType("normal");
    setIsChapterModalOpen(true);
  };
  
const handleEditChapter = (chapter) => {
  setEditChapter(chapter); // Existing chapter
  setChapterType("normal");
  setIsChapterModalOpen(true);
};
const handleChapterSubmit = async (e) => {
  e.preventDefault();
  const form = e.target;
  setIsLoading(true);
  try {
    if (editChapter) {
      const nextTitle = form.title.value;
      if (chapterTitleExists(nextTitle, editChapter.id)) {
        alert("Chapter title already exists. Please use a different title.");
        setIsLoading(false);
        return;
      }
      const data = {
        title: nextTitle,
        description: form.description.value
      };
      // PATCH existing chapter
      await axiosInstance.patch(`/admin/chapters/${editChapter.id}/`, data);
    } else {
      if (chapterType === "onboarding" || chapterType === "introduction") {
        const selectedSpecialChapter =
          chapterType === "onboarding"
            ? {
                label: "Onboarding chapter",
                chapterTitle: ONBOARDING_CHAPTER_TITLE,
                chapterDescription: ONBOARDING_CHAPTER_DESCRIPTION,
                requiresVideoLink: true,
                videoMeta: ONBOARDING_VIDEO_META,
                alreadyExists: hasOnboardingChapter,
              }
            : {
                label: "Introduction(Common section)",
                chapterTitle: INTRODUCTION_CHAPTER_TITLE,
                chapterDescription: INTRODUCTION_CHAPTER_DESCRIPTION,
                requiresVideoLink: false,
                alreadyExists: hasIntroductionChapter,
              };

        if (selectedSpecialChapter.alreadyExists) {
          alert(`${selectedSpecialChapter.label} already exists. You can create only one.`);
          setIsLoading(false);
          return;
        }

        const createdChapter = await axiosInstance.post("/admin/chapters/", {
          title: selectedSpecialChapter.chapterTitle,
          description: selectedSpecialChapter.chapterDescription
        });
        if (selectedSpecialChapter.requiresVideoLink) {
          const videoLink = form.video_link.value;
          if (!videoLink) {
            alert(`Vimeo link is required for ${selectedSpecialChapter.label}.`);
            setIsLoading(false);
            return;
          }

          const chapterId = createdChapter.data.id;
          const payload = {
            vimeo_url: videoLink,
            video_url: videoLink,
            ...selectedSpecialChapter.videoMeta,
            chapter: chapterId,
          };

          const endpoint = `/admin/chapters/${chapterId}/add-existing-video/`;
          const fallbackEndpoint = `/admin/chapters/${chapterId}/videos/`;

          try {
            await axiosInstance.post(endpoint, payload);
          } catch (err) {
            if (err?.response?.status === 404) {
              await axiosInstance.post(fallbackEndpoint, payload);
            } else {
              throw err;
            }
          }

          alert(`${selectedSpecialChapter.label} created with Vimeo video link.`);
        } else {
          alert(`${selectedSpecialChapter.label} created successfully.`);
        }
      } else {
        if (chapterTitleExists(form.title.value)) {
          alert("Chapter title already exists. Please use a different title.");
          setIsLoading(false);
          return;
        }
        const data = {
          title: form.title.value,
          description: form.description.value
        };
        // POST new chapter
        await axiosInstance.post("/admin/chapters/", data);
        alert("Chapter created successfully!");
      }
    }

    // Reload chapters list
    const res = await axiosInstance.get("/admin/chapters/");
    setChapters(res.data);

    setIsChapterModalOpen(false); // close modal
  } catch (error) {
    console.error("Error saving chapter:", error.response?.data || error.message);
    alert("Failed to save chapter.");
  }
  finally {
    setIsLoading(false); // Stop the loader
  }
};

  const handleDeleteChapter = async (chapterId) => {
      if (!window.confirm("Are you sure you want to delete this chapter?")) {
        return;
      }
      setIsLoading(true);
      try {
        await axiosInstance.delete(`/admin/chapters/${chapterId}/`);
        
        // Refresh chapters list after delete
        const res = await axiosInstance.get("/admin/chapters/");
        setChapters(res.data);
    
        alert("Chapter deleted successfully!");
      } catch (error) {
        console.error("Failed to delete chapter:", error.response?.data || error.message);
        alert("Failed to delete chapter.");
      }
      finally {
        setIsLoading(false); // Stop the loader
      }
    };
  
  
    const handleSubmit = async (e) => {
      e.preventDefault();
      const form = e.target;
    
      const title = form.title.value;
      const description = form.description.value;
      const language = form.language.value;
      const isFree = form.is_free.checked;
      const chapterId = activeChapter.id;
    
      const videoFile = form.video_file.files[0];
    
      if (!videoFile) {
        alert("Video file is required.");
        return;
      }
    
      if (!videoFile.type.startsWith("video/")) {
        alert("Uploaded file must be a video.");
        return;
      }
    
      try {
        const uploadRes = await axiosInstance.post(
          `/admin/chapters/videos/get-upload-link/`,
          {
            title,
            description,
            file_size: videoFile.size,
          }
        );
    
        const upload_link = uploadRes.data['upload_link'];
        const vimeo_video_uri = uploadRes.data['video_uri'];
    
        const upload = new Upload(videoFile, {
          chunkSize: 5242880,
          uploadUrl: upload_link,
          retryDelays: [0, 1000, 3000, 5000],
          metadata: {
            filename: videoFile.name,
            filetype: videoFile.type,
          },
          onError: function (error) {
            console.error("Upload failed:", error);
            alert("Upload to Vimeo failed.");
            setUploadProgress(null);
            setIsUploading(false);
          },
          onProgress: function (bytesUploaded, bytesTotal) {
            const percentage = Math.floor((bytesUploaded / bytesTotal) * 100);
            setUploadProgress(percentage);
          },
          onSuccess: async function () {
            console.log("Upload finished:", upload.url);
            alert("Video uploaded! Please wait for video to finish processing.");
            setUploadProgress(null);
            //setIsLoading(true);
    
            const checkTranscodeStatus = async ( delay = 5000) => {
                const VIMEO_ACCESS_TOKEN = 'f9942a4cce1acf3aa4597675404e972e';
                let status=null
                let count=0
                while (status!="complete") {
                  try {
                    const videoInfo = await axios.get(`https://api.vimeo.com${vimeo_video_uri}`, {
                      headers: { Authorization: `Bearer ${VIMEO_ACCESS_TOKEN}` },
                    });

                    status = videoInfo.data.transcode?.status;
                    setTransStatus(status)
                    console.log(`[Attempt ${count}] Transcode status: ${status}`);

                    if (status === "complete") return true;
                    if (status === "error") throw new Error("Vimeo transcode failed");

                    // wait and retry
                    await new Promise((resolve) => setTimeout(resolve, delay));
                  } catch (err) {
                    console.warn(`[Attempt ${count}] Error checking transcode:`, err.message);

                    // only return false after all retries fail
                    //if (attempt === retries) return false;

                    await new Promise((resolve) => setTimeout(resolve, delay));
                  }
                  count+=1
                }
                return false;
              };
    
            const transcodeReady = await checkTranscodeStatus();
            
            if (!transcodeReady) {
              alert("Video uploaded, but Vimeo transcode not completed. Try again later.");
              //setIsLoading(false);
              setIsUploading(false);
              return;
            }
            try {
              const metaForm = new FormData();
              metaForm.append("title", title);
              metaForm.append("description", description);
              metaForm.append("language", language);
              metaForm.append("is_free", isFree ? "True" : "False");
              metaForm.append("chapter", chapterId);
              metaForm.append("video_uri", vimeo_video_uri);
          
              const endpoint = `/admin/chapters/${chapterId}/videos/`;
              const headers = { "Content-Type": "multipart/form-data" };
          
              if (editVideo) {
                await axiosInstance.patch(endpoint, metaForm, { headers });
              } else {
                await axiosInstance.post(endpoint, metaForm, { headers });
              }
          
              const res = await axiosInstance.get(endpoint);
              setVideoData(res.data);
              setIsModalOpen(false);
            } catch (err) {
              console.error("Failed to save video metadata:", err);
              alert("Saving metadata failed.");
            } finally {
              //setIsLoading(false);
              setIsUploading(false);
              setTransStatus(null) // ✅ Stop loader after everything is done
            }
          },
        });
        setIsUploading(true);
        upload.start();
      } catch (error) {
        console.error("Error during client-side upload:", error);
        alert("Failed to upload video.");
      }
    };
    
    const handleDeleteVideo = async (video) => {
      if (!window.confirm("Are you sure you want to delete this video?")) return;
      setIsLoading(true);
      try {
        await axiosInstance.delete(`/admin/chapters/${activeChapter.id}/videos/`, {
          data: { video_id: video },
        });
    
        // Refresh video list
        const res = await axiosInstance.get(`/admin/chapters/${activeChapter.id}/videos/`);
        setVideoData(res.data);
    
        alert("Video deleted successfully.");
      } catch (error) {
        console.error("Failed to delete video:", error.response?.data || error.message);
        alert("Failed to delete video.");
      }
      finally{
        setIsLoading(false);
      }
    };

  const handleToggleVideoAccess = async (video) => {
    if (!activeChapter?.id) return;

    const nextIsFree = !video.is_free;
    const rowKey = video.id ?? video.video_id ?? video.vimeo_video_id;
    setTogglingVideoId(rowKey);
    const candidateVideoIds = [
      video.id,
      video.video_id,
      video.vimeo_video_id,
    ].filter((value, index, self) => value !== undefined && value !== null && self.indexOf(value) === index);
    const attemptErrors = [];

    try {
      let responseData = null;

      for (const candidateVideoId of candidateVideoIds) {
        try {
          const res = await axiosInstance.patch(
            `/admin/chapters/${activeChapter.id}/videos/${candidateVideoId}/`,
            { is_free: nextIsFree }
          );
          responseData = res.data;
          break;
        } catch (err) {
          attemptErrors.push({
            endpoint: `/admin/chapters/${activeChapter.id}/videos/${candidateVideoId}/`,
            status: err?.response?.status,
            data: err?.response?.data,
          });
          if (err?.response?.status === 404) continue;
          throw err;
        }
      }

      if (!responseData) {
        try {
          // Legacy fallback: some deployments accept PATCH on chapter videos endpoint with video_id in body.
          const fallbackVideoId = video.vimeo_video_id ?? video.id ?? video.video_id;
          if (fallbackVideoId !== undefined && fallbackVideoId !== null) {
            const fallbackRes = await axiosInstance.patch(
              `/admin/chapters/${activeChapter.id}/videos/`,
              { video_id: fallbackVideoId, is_free: nextIsFree }
            );
            responseData = fallbackRes.data;
          }
        } catch (fallbackError) {
          attemptErrors.push({
            endpoint: `/admin/chapters/${activeChapter.id}/videos/`,
            status: fallbackError?.response?.status,
            data: fallbackError?.response?.data,
          });
        }
      }

      if (!responseData) {
        console.error("Toggle API 404 diagnostics", {
          activeChapterId: activeChapter?.id,
          videoSnapshot: {
            id: video?.id,
            video_id: video?.video_id,
            vimeo_video_id: video?.vimeo_video_id,
            chapter: video?.chapter,
            title: video?.title,
          },
          attempts: attemptErrors,
        });
        alert("Video or chapter not found. Check console logs for ID diagnostics.");
        return;
      }

      const targetIds = [video.id, video.video_id, video.vimeo_video_id].filter(
        (value, index, self) => value !== undefined && value !== null && self.indexOf(value) === index
      );

      setVideoData((prev) =>
        prev.map((item) => {
          const itemIds = [item.id, item.video_id, item.vimeo_video_id];
          const isTargetVideo = itemIds.some((itemId) => targetIds.includes(itemId));
          return isTargetVideo
            ? { ...item, ...responseData, is_free: responseData?.is_free ?? nextIsFree }
            : item;
        })
      );

      try {
        const refreshedVideos = await axiosInstance.get(`/admin/chapters/${activeChapter.id}/videos/`);
        setVideoData(refreshedVideos.data);
      } catch (refreshError) {
        console.warn("Toggle succeeded but video list refresh failed:", refreshError?.response?.data || refreshError.message);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400) {
        alert("Invalid request: `is_free` is missing or invalid.");
      } else if (status === 403) {
        alert("Not authorized. Admin access is required.");
      } else if (status === 404) {
        alert("Video or chapter not found.");
      } else {
        alert("Failed to update Free/Paid status.");
      }
      console.error("Failed to toggle video access:", error?.response?.data || error.message);
    } finally {
      setTogglingVideoId(null);
    }
  };
    
  
  const openVideoModal = (video) => {
    setVideoToPlay(video);
  };

  return (
    <div className="chapter-container">
      {isLoading && <SyncLoader />} 
      {!showVideos ? (
        <>
          <h1>📚 Chapters and Videos</h1>
          <button className="add-btn" onClick={handleAddChapter}>+ Add Chapter</button>
           <div className="search-bar">
        <input
          type="text"
          placeholder="Search chapters..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>
          <div className="chapter-table-wrapper">
            <table className="chapter-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((chapter, index) => (
                  <tr
                    key={chapter.id}
                    className={`${
                      getChapterKind(chapter.title) !== "normal"
                        ? `special-chapter-row ${getChapterKind(chapter.title)}-chapter-row`
                        : ""
                    }`}
                  >
                    <td>{indexOfFirst + index + 1}</td>
                    <td>
                      {chapter.title}
                      {getChapterKind(chapter.title) === "onboarding" && (
                        <span className="special-chapter-badge onboarding-badge">Pinned</span>
                      )}
                      {getChapterKind(chapter.title) === "introduction" && (
                        <span className="special-chapter-badge introduction-badge">Pinned</span>
                      )}
                    </td>
                    <td>{chapter.description}</td>
                    <td>
                      <button className="open-btn" onClick={() => handleOpenChapter(chapter)}>
                        Open
                      </button>
                      <button className="edit-btn" onClick={() => handleEditChapter(chapter)}>Edit</button>
                      <button className="delete-btn" onClick={() => handleDeleteChapter(chapter.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
  <div className="pagination">
    <button
      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
      disabled={currentPage === 1}
    >
      Prev
    </button>

    {[...Array(totalPages)].map((_, idx) => {
      const pageNum = idx + 1;
      return (
        <button
          key={pageNum}
          className={currentPage === pageNum ? "active" : ""}
          onClick={() => setCurrentPage(pageNum)}
        >
          {pageNum}
        </button>
      );
    })}

    <button
      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
      disabled={currentPage === totalPages}
    >
      Next
    </button>
  </div>
)}
          {isChapterModalOpen && (
          <div className="modal">
            <form onSubmit={handleChapterSubmit} className="modal-content">
              <h3>{editChapter ? "Edit Chapter" : "Add New Chapter"}</h3>
              {!editChapter && (
                <>
                  <label>Chapter Type</label>
                  <select
                    value={chapterType}
                    onChange={(e) => setChapterType(e.target.value)}
                  >
                    <option value="normal">Normal Chapter</option>
                    <option value="onboarding" disabled={hasOnboardingChapter}>
                      Onboarding Chapter {hasOnboardingChapter ? "(Already Created)" : ""}
                    </option>
                    <option value="introduction" disabled={hasIntroductionChapter}>
                      Introduction(Common section) {hasIntroductionChapter ? "(Already Created)" : ""}
                    </option>
                  </select>
                </>
              )}

              {editChapter || chapterType === "normal" ? (
                <>
                  <input
                    name="title"
                    defaultValue={editChapter?.title || ""}
                    placeholder="Title"
                    required
                  />
                  <input
                    name="description"
                    defaultValue={editChapter?.description || ""}
                    placeholder="Description"
                    required
                  />
                </>
              ) : (
                <>
                  <div className="note-box">
                    {specialChapterConfig?.label} fields are hardcoded:
                    <br />
                    Chapter Title: <strong>{specialChapterConfig?.chapterTitle}</strong>
                    <br />
                    Chapter Description: <strong>{specialChapterConfig?.chapterDescription}</strong>
                    {specialChapterConfig?.requiresVideoLink && (
                      <>
                        <br />
                        Video Title: <strong>{specialChapterConfig?.videoMeta.title}</strong>
                        <br />
                        Video Description: <strong>{specialChapterConfig?.videoMeta.description}</strong>
                        <br />
                        Language: <strong>{specialChapterConfig?.videoMeta.language}</strong>
                        <br />
                        Free: <strong>{specialChapterConfig?.videoMeta.is_free ? "Yes" : "No"}</strong>
                      </>
                    )}
                  </div>
                  {specialChapterConfig?.requiresVideoLink ? (
                    <input
                      name="video_link"
                      placeholder={specialChapterConfig?.videoLinkPlaceholder}
                      required
                    />
                  ) : (
                    <div className="note-box">
                      No video link required. This will create only the chapter.
                    </div>
                  )}
                </>
              )}

              <div className="modal-actions">
                <button type="submit" className="save-btn">Save</button>
                <button type="button" className="cancel-btn" onClick={() => setIsChapterModalOpen(false)}>Cancel</button>
              </div>
            </form>
          </div>

          
        )}

        </>
      ) : (
        <>
          <h2>Videos for: {activeChapter.title}</h2>
          <div className="top-bar">
            <button onClick={handleBack} className="back-btn">← Back</button>
            <button onClick={handleAddVideo} className="add-btn">+ Add Video</button>
          </div>
          <div className="chapter-table-wrapper">
            <table className="chapter-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Thumbnail</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Language</th>
                  <th>Free/Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {videoData.map((video, index) => {
                  const rowKey = video.id ?? video.video_id ?? video.vimeo_video_id;
                  const isToggling = togglingVideoId === rowKey;
                  return (
                  <tr key={rowKey}>
                    <td>{index + 1}</td>
                    <td><img src={video.thumbnail_url} alt="thumb" className="thumbnail" /></td>
                    <td>{video.title}</td>
                    <td>{video.description}</td>
                    <td>{video.language}</td>
                    <td>{video.is_free ? "Free" : "Paid"}</td>
                    <td>
                      {/* <button className="edit-btn" onClick={() => handleEditVideo(video)}>Edit</button> */}
                      <button
                        className="edit-btn"
                        onClick={() => handleToggleVideoAccess(video)}
                        disabled={isToggling}
                      >
                        {isToggling
                          ? "Updating..."
                          : video.is_free
                          ? "Set Paid"
                          : "Set Free"}
                      </button>
                      <button className="delete-btn" onClick={() => handleDeleteVideo(video.vimeo_video_id)}>Delete</button>
                      <button className="watch-btn" onClick={() => openVideoModal(video)}>▶ Watch</button>
                    </td>
                  </tr>
                    );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      {isModalOpen && (
        <div className="modal">
    {!uploadByLink ? (
          <form onSubmit={handleSubmit} className="modal-content" encType="multipart/form-data">
            <div className="tabs">
      <button
        className={`tab ${uploadByLink ? "" : "active"}`}
        onClick={() => setUploadByLink(false)}
        disabled={isUploading}
      >
        File Upload
      </button>
      <button
        className={`tab ${uploadByLink ? "active" : ""}`}
        onClick={() => setUploadByLink(true)}
        disabled={isUploading}
      >
        Upload by Video Link
      </button>
    </div>
          <h3>{editVideo ? "Edit Video" : "Add New Video file"}</h3>
          <input name="title" defaultValue={editVideo?.title} placeholder="Title" required />
          <input name="description" defaultValue={editVideo?.description} placeholder="Description" required />
          <select name="language" defaultValue={editVideo?.language || "en"} required>
            {LANGUAGE_OPTIONS.map((lang) => (
              <option key={lang.value} value={lang.value}>
                {lang.label}
              </option>
            ))}
          </select>
          {/* <label>Thumbnail Image (Only .jpg/.png/.jpeg)</label>
          <input name="thumbnail_file" type="file" accept="image/*" /> */}
          <label>Video File (Only .mp4/.mov etc.)</label>
          <input name="video_file" type="file" accept="video/*" />
          <label>
            <input name="is_free" type="checkbox" defaultChecked={editVideo?.is_free} />
            Is Free
          </label>
        {uploadProgress !== null && (
          <div className="progress-bar-wrapper">
            <div className="progress-bar" style={{ width: `${uploadProgress}%` }}>
              {uploadProgress}%
            </div>
          </div>
          )}
          {transstatus !== null && (
  transstatus === "complete" ? (
    <div>
      Transcoding {transstatus}d. Thanks for your patience 😊
    </div>
  ) : (
    <div>
      Transcoding of video is {transstatus}. Please don't close or navigate until it completes.
    </div>
  )
        
        )
        }
          {transstatus === null && (
          <div className="modal-actions">
            <button type="submit" className="save-btn">Save</button>
            <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false) } disabled={isUploading}>Cancel</button>
          </div>
        )}
        </form>):
        (
        <form onSubmit={handleLinkUpload} className="modal-content">
          <div className="tabs">
      <button
        className={`tab ${uploadByLink ? "" : "active"}`}
        onClick={() => setUploadByLink(false)}
        disabled={isUploading}
      >
        File Upload
      </button>
      <button
        className={`tab ${uploadByLink ? "active" : ""}`}
        onClick={() => setUploadByLink(true)}
        disabled={isUploading}
      >
        Upload by Video Link
      </button>
    </div>
        <h3>Add Video by Link</h3>
        <input name="title" placeholder="Title" required />
        <input name="description" placeholder="Description" required />
        <select name="language" defaultValue="en" required>
          {LANGUAGE_OPTIONS.map((lang) => (
            <option key={lang.value} value={lang.value}>
              {lang.label}
            </option>
          ))}
        </select>
        <input name="video_link" placeholder="Video Link (Vimeo URL)" required />
        <label>
          <input name="is_free" type="checkbox" />
          Is Free
        </label>
        <div class="note-box">
  <strong>Note:</strong> Before uploading the Vimeo link, please make sure your video is <em>accessible by anyone</em>.
</div>
        <div className="modal-actions">
          <button type="submit" className="save-btn">Save</button>
          <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)} disabled={isUploading}>Cancel</button>
        </div>
      </form>
    )}
        </div>
      )}

      {videoToPlay && (
        <div className="modal">
          <div className="modal-content">
            <h3>{videoToPlay.title}</h3>
            <iframe
              src={videoToPlay.vimeo_video_url}
              width="100%"
              height="360"
              frameBorder="0"
              allow="autoplay; fullscreen"
              allowFullScreen
              title={videoToPlay.title}
            ></iframe>
            <button className="cancel-btn" onClick={() => setVideoToPlay(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChapterTable;
